import { NgClass } from '@angular/common';
import { Component, computed, input, output } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { HeaderFieldComponent, HeaderGroupComponent, HeaderItemComponent } from '@vectronic/ui';
import moment from 'moment';
import { Subject, debounceTime, merge } from 'rxjs';
import { AbstractScheduleService } from '../../services/abstract-schedule.service';
import {
  ScheduleRangeSliderComponent,
  ScheduleRangeSliderValue,
} from '../schedule-range-slider/schedule-range-slider.component';
import { SequencePreviewComponent } from '../sequence-preview/sequence-preview.component';

@Component({
  selector: 'rc-sequence',
  templateUrl: './sequence.component.html',
  styleUrls: ['./sequence.component.scss'],
  imports: [
    HeaderGroupComponent,
    HeaderItemComponent,
    HeaderFieldComponent,
    MatTooltip,
    MatIconButton,
    MatIcon,
    SequencePreviewComponent,
    ScheduleRangeSliderComponent,
    NgClass,
  ],
})
export class SequenceComponent {
  fixRate = input<number>();
  isRuleExpanded = input<boolean>();
  duration = input<number>();
  offset = input<number>();
  periodLength = input<number>();
  ruleIndex = input<number>();
  sequenceIndex = input<number>();
  startDate = input<string>();
  skipPosition = input<number>();
  isDeletable = input<boolean>();
  scheduleService = input<AbstractScheduleService>();
  isCommunication = input<boolean>();
  readonly = input<boolean>();
  isBeacon = input<boolean>();
  hasTransmissionCount = input<boolean>();

  remove = output<number>();
  clickSummary = output();

  fixRateHourFormControl = new FormControl<number>(undefined);
  fixRateDayFormControl = new FormControl<number>(undefined);
  fixRateMinuteFormControl = new FormControl<number>(undefined);
  startWeekdayFormControl = new FormControl('');
  startHourFormControl = new FormControl<number>(undefined);
  startMinuteFormControl = new FormControl<number>(undefined);
  endWeekdayFormControl = new FormControl('');
  endHourFormControl = new FormControl<number>(undefined);
  endMinuteFormControl = new FormControl<number>(undefined);

  showFixRate = false;
  showStart = false;
  showEnd = false;
  weekdays: {
    value: string;
    label: string;
  }[];

  lastStartWeekday = '';
  lastEndWeekday = '';

  private sliderChanges$ = new Subject<ScheduleRangeSliderValue>();

  constructor() {
    this.sliderChanges$
      .pipe(takeUntilDestroyed(), debounceTime(500))
      .subscribe((value) => this.updateSequenceFromSlider(value));

    toObservable(this.fixRate)
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        const fixRate = Math.min(this.fixRateInMinutes(), this.periodLength(), 31 * 60 * 24);
        const minutes = fixRate % 60;
        const days = Math.floor(fixRate / 1440);
        const hours = (fixRate - days * 1440 - minutes) / 60;
        this.fixRateHourFormControl.setValue(hours);
        this.fixRateMinuteFormControl.setValue(minutes);
        this.fixRateDayFormControl.setValue(days);
      });

    toObservable(this.startDate)
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        const startDay = moment(this.startDate()).day();
        const weekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
        this.weekdays = weekdays.map((v, i) => {
          const index = (startDay + i + 6) % 7;
          return { label: weekdays[index], value: weekdays[index] };
        });
      });

    merge(toObservable(this.offset), toObservable(this.startDate), toObservable(this.duration))
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.updateStartAndEndFormControls());

    toObservable(this.isRuleExpanded)
      .pipe(takeUntilDestroyed())
      .subscribe((expanded) => {
        if (expanded === false) {
          this.showFixRate = false;
          this.showStart = false;
          this.showEnd = false;
        }
      });
  }

  updateStartAndEndFormControls() {
    if (this.isWeekly()) {
      const startOffset = moment(this.startDate())
        .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
        .add({ minute: this.offset() });
      this.startWeekdayFormControl.setValue(startOffset.format('ddd'));
      this.startHourFormControl.setValue(parseInt(startOffset.format('H')));
      this.startMinuteFormControl.setValue(parseInt(startOffset.format('m')));
      const endOffset = moment(this.startDate())
        .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
        .add({ minute: this.offset() + this.duration() });
      this.endWeekdayFormControl.setValue(endOffset.format('ddd'));
      this.endHourFormControl.setValue(parseInt(endOffset.format('H')));
      this.endMinuteFormControl.setValue(parseInt(endOffset.format('m')));
    } else {
      const startMinute = this.offset() % 60;
      const startHour = Math.floor(this.offset() / 60);
      this.startWeekdayFormControl.setValue('');
      this.startHourFormControl.setValue(startHour);
      this.startMinuteFormControl.setValue(startMinute);
      const endTotal = this.offset() + this.duration();
      let endMinute: number;
      let endHour: number;
      if (this.isWeeklyOrDaily() && endTotal > this.periodLength()) {
        const wrappedEnd = endTotal - this.periodLength();
        endMinute = wrappedEnd % 60;
        endHour = Math.floor(wrappedEnd / 60);
      } else {
        endMinute = endTotal % 60;
        endHour = Math.floor(endTotal / 60);
        if (endMinute === 0 && endHour === 0 && this.isWeeklyOrDaily()) {
          endHour = 24;
        } else if (endMinute !== 0 && endHour === 24 && this.isWeeklyOrDaily()) {
          endMinute = 0;
        }
      }
      this.endWeekdayFormControl.setValue('');
      this.endHourFormControl.setValue(endHour);
      this.endMinuteFormControl.setValue(endMinute);
    }
  }

  onSliderValueChange(value: ScheduleRangeSliderValue) {
    this.sliderChanges$.next(value);
  }

  updateSequenceFromSlider({ offset, duration }: ScheduleRangeSliderValue) {
    if (offset !== this.offset() || duration !== this.duration()) {
      const fixRate = this.getFixRateMinutesFromControls();
      this.scheduleService()?.updateSequence(
        this.ruleIndex(),
        this.sequenceIndex(),
        {
          fixRate: Math.min(fixRate, this.periodLength(), 31 * 60 * 24) * 60,
          duration,
          offset,
        },
        this.readonly(),
      );
    }
  }

  private getFixRateMinutesFromControls(): number {
    return (
      parseInt(this.fixRateDayFormControl.value as any) * 60 * 24 +
      parseInt(this.fixRateHourFormControl.value as any) * 60 +
      parseInt(this.fixRateMinuteFormControl.value as any)
    );
  }

  updateStart() {
    if (!this.startWeekdayFormControl || !this.startHourFormControl || !this.startMinuteFormControl)
      return;
    let newOffset;
    if (this.isWeekly()) {
      const weekdayIndex = Math.max(
        0,
        this.weekdays.findIndex((item) => item.value === this.startWeekdayFormControl.value),
      );
      newOffset =
        weekdayIndex * 1440 +
        parseInt(this.startHourFormControl.value as any) * 60 +
        parseInt(this.startMinuteFormControl.value as any);
    } else {
      newOffset =
        parseInt(this.startHourFormControl.value as any) * 60 +
        parseInt(this.startMinuteFormControl.value as any);
    }
    const minDuration = 1;
    const periodLength = this.periodLength();
    const endMinutes = this.offset() + this.duration();
    const wrappedEnd = endMinutes > periodLength ? endMinutes - periodLength : 0;
    newOffset = Math.max(wrappedEnd, Math.min(newOffset, periodLength - minDuration));
    const duration = Math.max(minDuration, endMinutes - newOffset);
    this.scheduleService()?.updateSequenceOffsetDuration(
      this.ruleIndex(),
      this.sequenceIndex(),
      newOffset,
      duration,
      this.readonly(),
    );
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  updateEnd() {
    if (!this.endWeekdayFormControl || !this.endHourFormControl || !this.endMinuteFormControl)
      return;

    let hour = parseInt(this.endHourFormControl.value as any);
    let minute = parseInt(this.endMinuteFormControl.value as any);
    if (isNaN(hour)) hour = 0;
    if (isNaN(minute)) minute = 0;

    if (hour > 24 && this.isWeeklyOrDaily()) hour = 24;
    if (hour < 0) hour = 0;

    if (hour === 24 && minute !== 0 && this.isWeeklyOrDaily()) {
      minute = 0;
      this.endHourFormControl.setValue(24);
      this.endMinuteFormControl.setValue(0);
    }

    if (!this.isWeeklyOrDaily() && hour * 60 + minute > this.periodLength()) {
      hour = Math.floor(this.periodLength() / 60);
      minute = this.periodLength() % 60;
      this.endHourFormControl.setValue(hour);
      this.endMinuteFormControl.setValue(minute);
    }

    let end;
    if (this.isWeekly()) {
      let weekdayIndex = Math.max(
        0,
        this.weekdays.findIndex((item) => item.value === this.endWeekdayFormControl.value),
      );
      if (weekdayIndex === 0 && hour === 0 && minute === 0) {
        weekdayIndex = 7;
      }
      end = weekdayIndex * 1440 + hour * 60 + minute;
    } else {
      end = hour * 60 + minute;
    }

    if (this.isWeeklyOrDaily()) {
      const offset = this.offset();
      if (end < offset || end === offset) {
        end += this.periodLength();
      }
    } else {
      end = Math.min(end, this.periodLength());
    }

    const minDuration = 1;
    const rawDuration = end - this.offset();
    if (rawDuration < minDuration) {
      this.updateStartAndEndFormControls();
    }
    const duration = Math.max(minDuration, rawDuration);
    this.scheduleService()?.updateSequenceOffsetDuration(
      this.ruleIndex(),
      this.sequenceIndex(),
      this.offset(),
      duration,
      this.readonly(),
    );
  }

  getDateString(value: number): string {
    if (value === 0) return '00:00';
    const minutes = value % 60;
    const hours = (value - minutes) / 60;
    const hourString = hours < 10 ? `0${hours}` : hours;
    const minuteString = minutes < 10 ? `0${minutes}` : minutes;
    return `${hourString}:${minuteString}`;
  }

  getGeneralString(fix: number): string {
    if (this.isWeekly()) {
      return moment(this.startDate())
        .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
        .add({ minute: fix })
        .format('ddd HH:mm');
    } else {
      return this.getDateString(fix);
    }
  }

  onClickRemoveSequence() {
    this.remove.emit(this.sequenceIndex());
  }

  getDurationString(value: number): string {
    if (value === 0) return '0m';
    const minutes = value % 60;
    const days = Math.floor(value / 1440);
    const hours = (value - days * 1440 - minutes) / 60;
    if (minutes && !hours && !days) {
      return `${minutes} min`;
    }
    return `${days ? days + 'd ' : ''}${hours ? hours + 'h ' : ''}${minutes ? minutes + 'm ' : ''}`;
  }

  getStartString(): string {
    if (this.isWeekly()) {
      return moment(this.startDate())
        .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
        .add({ minute: this.offset() })
        .format('ddd HH:mm');
    } else {
      return this.getDateString(this.offset());
    }
  }

  getEndString(): string {
    if (this.isWeekly()) {
      return moment(this.startDate())
        .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
        .add({ minute: this.offset() + this.duration() })
        .format('ddd HH:mm');
    } else {
      const endTotal = this.offset() + this.duration();
      if (this.isWeeklyOrDaily() && endTotal > this.periodLength()) {
        return `${this.getDateString(endTotal - this.periodLength())} (+1d)`;
      }
      return this.getDateString(endTotal);
    }
  }

  isWeekly = computed(() => {
    return this.periodLength() === 10080;
  });

  isWeeklyOrDaily = computed(() => {
    return this.periodLength() === 10080 || this.periodLength() === 1440;
  });

  onStartWeekdayChanged(value: string | any) {
    if (this.lastStartWeekday !== value) {
      this.lastStartWeekday = value;
      this.updateStart();
    }
  }

  onEndWeekdayChanged(value: string | any) {
    if (this.lastEndWeekday !== value) {
      this.lastEndWeekday = value;
      this.updateEnd();
    }
  }

  onFixRateOpenChange(isOpen: boolean) {
    if (!isOpen) {
      this.updateFixRate();
    }
  }

  updateFixRate() {
    let fixRate = this.getFixRateMinutesFromControls();
    if (fixRate === 0) fixRate = 120;
    const offset = this.offset();
    const duration = this.duration();
    if (fixRate > this.periodLength() || fixRate > 31 * 60 * 24) {
      const newFixRate = Math.min(this.fixRateInMinutes(), this.periodLength(), 31 * 60 * 24);
      const minutes = newFixRate % 60;
      const days = Math.floor(newFixRate / 1440);
      const hours = (newFixRate - days * 1440 - minutes) / 60;
      this.fixRateHourFormControl.setValue(hours);
      this.fixRateMinuteFormControl.setValue(minutes);
      this.fixRateDayFormControl.setValue(days);
    }
    fixRate = Math.min(fixRate, this.periodLength(), 31 * 60 * 24) * 60;
    const fixRateInMinutes = fixRate / 60;
    const minutes = fixRateInMinutes % 60;
    const days = Math.floor(fixRateInMinutes / 1440);
    const hours = (fixRateInMinutes - days * 1440 - minutes) / 60;

    this.fixRateHourFormControl.setValue(hours);
    this.fixRateMinuteFormControl.setValue(minutes);
    this.fixRateDayFormControl.setValue(days);

    this.scheduleService()?.updateSequence(
      this.ruleIndex(),
      this.sequenceIndex(),
      { fixRate, duration, offset },
      this.readonly(),
    );
  }

  getFixRateString() {
    if (
      (!this.fixRateDayFormControl.value && this.fixRateDayFormControl.value != 0) ||
      (!this.fixRateHourFormControl.value && this.fixRateHourFormControl.value != 0) ||
      (!this.fixRateMinuteFormControl.value && this.fixRateMinuteFormControl.value != 0)
    ) {
      return '';
    }
    const fixRate =
      parseInt(this.fixRateDayFormControl.value as any) * 60 * 24 +
      parseInt(this.fixRateHourFormControl.value as any) * 60 +
      parseInt(this.fixRateMinuteFormControl.value as any);
    return this.getDurationString(fixRate);
  }

  fixRateInMinutes = computed(() => {
    return this.fixRate() / 60;
  });
}
